import type { Key9Result } from '../types/puzzle';

export const STORAGE_KEY = 'daysudoku:key9:v1';

export type DaySudokuPhase =
  | 'key9_in_progress'
  | 'key9_unlocked'
  | 'full_in_progress'
  | 'full_done'
  | 'missed';

export type DayProgress = {
  phase: DaySudokuPhase;
  key9Result?: Key9Result;
  fullBoard?: string[];
  fullSeconds?: number;
};

export type DaySudokuSave = {
  lastDate?: string;
  currentStreak: number;
  bestStreak: number;
  results: Record<string, Key9Result>;
  days: Record<string, DayProgress>;
};

export function loadSave(): DaySudokuSave {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptySave();
    return normalizeSave(JSON.parse(raw) as Partial<DaySudokuSave>);
  } catch {
    return emptySave();
  }
}

export function saveKey9Unlock(date: string, result: Key9Result): DaySudokuSave {
  const state = loadSave();
  const previous = state.days[date];
  if (state.results[date]?.status === 'missed' || previous?.key9Result?.status === 'missed') {
    return writeSave(state);
  }
  state.results[date] = result;
  state.days[date] = {
    ...previous,
    phase: previous?.phase === 'full_done' ? 'full_done' : 'key9_unlocked',
    key9Result: result
  };
  return writeSave(state);
}

export function saveKey9Missed(date: string, result: Key9Result): DaySudokuSave {
  const state = loadSave();
  state.results[date] = result;
  state.days[date] = {
    phase: 'missed',
    key9Result: result
  };
  return writeSave(state);
}

export function saveFullProgress(date: string, fullBoard: string[]): DaySudokuSave {
  const state = loadSave();
  const previous = state.days[date];
  state.days[date] = {
    ...previous,
    phase: previous?.phase === 'full_done' ? 'full_done' : 'full_in_progress',
    fullBoard: [...fullBoard]
  };
  return writeSave(state);
}

export function saveFullDone(date: string, fullBoard: string[], seconds: number): DaySudokuSave {
  const state = loadSave();
  const previous = state.days[date];
  if (previous?.phase !== 'full_done') {
    state.currentStreak = calculateNextStreak(state.lastDate, date, state.currentStreak);
    state.bestStreak = Math.max(state.bestStreak, state.currentStreak);
    state.lastDate = date;
  }

  state.days[date] = {
    ...previous,
    phase: 'full_done',
    fullBoard: [...fullBoard],
    fullSeconds: seconds
  };
  return writeSave(state);
}

export function saveResult(date: string, result: Key9Result): DaySudokuSave {
  return result.status === 'missed' ? saveKey9Missed(date, result) : saveKey9Unlock(date, result);
}

function emptySave(): DaySudokuSave {
  return { currentStreak: 0, bestStreak: 0, results: {}, days: {} };
}

function normalizeSave(raw: Partial<DaySudokuSave>): DaySudokuSave {
  const save: DaySudokuSave = {
    lastDate: raw.lastDate,
    currentStreak: Number(raw.currentStreak) || 0,
    bestStreak: Number(raw.bestStreak) || 0,
    results: raw.results ?? {},
    days: raw.days ?? {}
  };

  Object.entries(save.results).forEach(([date, result]) => {
    if (save.days[date]) return;
    save.days[date] = {
      phase: result.status === 'missed' ? 'missed' : 'key9_unlocked',
      key9Result: result
    };
  });

  return save;
}

function writeSave(state: DaySudokuSave): DaySudokuSave {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

function calculateNextStreak(lastDate: string | undefined, today: string, current: number): number {
  if (!lastDate) return 1;
  const last = new Date(`${lastDate}T00:00:00`);
  const now = new Date(`${today}T00:00:00`);
  const diffDays = Math.round((now.getTime() - last.getTime()) / 86400000);
  if (diffDays === 0) return current;
  if (diffDays === 1) return current + 1;
  return 1;
}
